import logoColor from '../assets/ms-logo-horizontal-color.svg';
import logoWhite from '../assets/ms-logo-horizontal-white.svg';
import { cn } from '../lib/utils';

/**
 * The Micro Strategies logo paired with the product name.
 *
 * The SVGs are the official 2025 primary horizontal lock-ups, copied verbatim
 * from the "2025 - Logo Files" pack. They render as <img> so nothing in this
 * app's CSS can recolour, stretch or otherwise alter them — the guidelines
 * forbid all of that.
 *
 * Two rules from the guidelines are encoded here and should not be relaxed:
 *
 *  - MINIMUM SIZE. The primary logo may never render below 150px wide in
 *    digital formats, so `width` is clamped. If a layout cannot afford 150px,
 *    drop the logo entirely rather than shrink it.
 *  - CLEAR SPACE. Nothing may come closer than the height of the lowercase "g"
 *    in "strategies" — roughly 14% of the lock-up's height. That is derived
 *    below, and the app header is sized to respect it.
 */

/** Aspect ratio of the primary horizontal lock-up (viewBox 420 x 128). */
const ASPECT = 420 / 128;

/** Guidelines: never below 150px wide in digital formats. */
export const MIN_LOGO_WIDTH = 150;

interface BrandMarkProps {
  /** `light` uses the white lock-up (dark grounds); `dark` the full-colour one. */
  tone?: 'light' | 'dark';
  /** Show the product name beside the logo. Hidden where space is tight. */
  showProductName?: boolean;
  /** Rendered logo width in px. Values below MIN_LOGO_WIDTH are clamped up. */
  width?: number;
  className?: string;
}

const BrandMark = ({
  tone = 'light',
  showProductName = true,
  width = MIN_LOGO_WIDTH,
  className,
}: BrandMarkProps) => {
  const renderedWidth = Math.max(width, MIN_LOGO_WIDTH);
  const clearSpace = Math.round((renderedWidth / ASPECT) * 0.14);

  return (
    <div
      className={cn('flex min-w-0 items-center', className)}
      style={{ gap: clearSpace, paddingLeft: clearSpace }}
    >
      <img
        src={tone === 'light' ? logoWhite : logoColor}
        alt="Micro Strategies"
        width={renderedWidth}
        height={renderedWidth / ASPECT}
        className="block shrink-0"
        style={{ width: renderedWidth, height: renderedWidth / ASPECT }}
      />
      {showProductName && (
        <>
          <span
            aria-hidden
            className={cn(
              'w-px self-stretch',
              tone === 'light' ? 'bg-white/30' : 'bg-line-strong'
            )}
            style={{ marginBlock: clearSpace }}
          />
          <span
            className={cn(
              'truncate font-sans text-base font-semibold',
              tone === 'light' ? 'text-white' : 'text-ink'
            )}
          >
            Race Control Center
          </span>
        </>
      )}
    </div>
  );
};

export default BrandMark;
